import { Banner, Page, Spinner } from "@shopify/polaris";
import { useAdmin } from "context/admin";
import { useUser } from "context/user";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { exportManualDeliveriesCsv, fetchManualDeliveries, fetchManualDeliveryContext } from "util/manualDeliveries";
import { formFromRow } from "util/manualDeliveryForm";
import { filterRows, groupRows, menuItems, roleOf, sentence } from "util/manualDeliveryStatus";
import { defaultRange } from "util/manualDeliveryTime";
import { useConfirm } from "./components/ConfirmDialog";
import DeliveryFilters from "./components/DeliveryFilters";
import DeliveryTable from "./components/DeliveryTable";
import NewDeliveryPanel from "./components/NewDeliveryPanel";
import RowMenu from "./components/RowMenu";
import Toast from "./components/Toast";
import useRowActions from "./components/useRowActions";
import "./ManualDeliveries.css";

// Menus show "Available in N min"; refresh "now" often enough for that to stay right.
const CLOCK_TICK_MS = 30000;
// What the panel left behind when it closed after a booking problem (NewDeliveryPanel onSaved kinds).
const SAVED_TOASTS = {
  unknown: { title: "Saved under Needs attention", text: "Check the courier before booking again." },
  unconfirmed: {
    title: "We can't tell if it was booked",
    text: "Check the list and the courier before booking it again.",
  },
  ownUnconfirmed: {
    title: "We couldn't confirm the delivery was saved",
    text: "Check the list before adding it again.",
  },
  notBooked: { title: "Saved as not booked", text: "Retry from the list whenever you are ready." },
};

export default function ManualDeliveriesPage() {
  const admin = useAdmin();
  const role = roleOf(useUser());
  const shopId = admin.selectedShopId;
  const [context, setContext] = useState(null);
  const [range, setRange] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [toast, setToast] = useState(null);
  const [menu, setMenu] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  // { seed: form | null }: what the New delivery panel opens with (seed is a "Copy as new delivery" prefill).
  const [panel, setPanel] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [confirmElement, confirm] = useConfirm();
  const menuRef = useRef(null);
  menuRef.current = menu;
  // The row whose open menu a press on its own trigger just closed, so the click that follows does not reopen it.
  const pressedOpenRef = useRef(null);
  const loadSeq = useRef(0);
  // Set once a list has loaded, so a failed reload keeps the last rows on screen under its banner.
  const [loadedOnce, setLoadedOnce] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const loadContext = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const next = await fetchManualDeliveryContext(shopId);
      setContext(next);
      setRange((current) => current || defaultRange(next.today));
    } catch (error) {
      setLoading(false);
      setLoadError(error.message || "We couldn't load this store's settings.");
    }
  }, [shopId]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  // quiet: refresh after an action without swapping the table for a spinner (keeps focus and scroll). Only the
  // newest request may set the rows, so a slow answer for an older date range never replaces a newer one.
  const load = useCallback(
    async (quiet = false) => {
      if (!shopId || !range) return;
      const seq = ++loadSeq.current;
      if (!quiet) setLoading(true);
      setLoadError(null);
      try {
        const next = await fetchManualDeliveries(shopId, range.from, range.to);
        if (seq === loadSeq.current) {
          setRows(next);
          setLoadedOnce(true);
        }
      } catch (error) {
        if (seq === loadSeq.current) setLoadError(error.message || "Something went wrong.");
      } finally {
        if (seq === loadSeq.current) setLoading(false);
      }
    },
    [shopId, range]
  );

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => filterRows(rows, { query, status }), [rows, query, status]);
  const groups = useMemo(() => (context ? groupRows(visible, context.today) : []), [visible, context]);

  const actions = useRowActions({
    shopId,
    reload: () => load(true),
    showToast: setToast,
    confirm,
    setHighlightId,
    openCopy: (row) => setPanel({ seed: formFromRow(row, context.today) }),
    todayIso: context ? context.today : null,
  });

  const pressMenu = (row) => {
    const current = menuRef.current;
    pressedOpenRef.current = current && current.row.id === row.id ? row.id : null;
  };
  const openMenu = (row, trigger) => {
    const closedByPress = pressedOpenRef.current === row.id;
    pressedOpenRef.current = null;
    if (closedByPress || (menu && menu.row.id === row.id)) {
      setMenu(null);
      return;
    }
    setMenu({ row, trigger, rect: trigger.getBoundingClientRect() });
  };
  const closeMenu = useCallback((returnFocus) => {
    const current = menuRef.current;
    setMenu(null);
    if (current && returnFocus && current.trigger) current.trigger.focus();
  }, []);
  const selectMenu = (action) => {
    const current = menuRef.current;
    setMenu(null);
    if (!current) return;
    if (current.trigger) current.trigger.focus();
    actions.run(current.row, action);
  };

  const onExport = async () => {
    const ids = [].concat(...groups.map((group) => group.rows)).map((row) => row.id);
    if (!ids.length) return;
    setExporting(true);
    try {
      const filename = await exportManualDeliveriesCsv(shopId, ids, context.today);
      setToast({
        title: `Exported ${ids.length} ${ids.length === 1 ? "delivery" : "deliveries"}`,
        text: `${filename} · matches your filters`,
      });
    } catch (error) {
      setToast({ title: "Export failed", text: sentence(error.message) });
    } finally {
      setExporting(false);
    }
  };

  let body = null;
  if (loading) {
    body = (
      <div className="md-loading">
        <Spinner accessibilityLabel="Loading deliveries" size="large" />
      </div>
    );
  } else if (context && (!loadError || loadedOnce)) {
    body = (
      <DeliveryTable
        groups={groups}
        role={role}
        nowMs={nowMs}
        todayIso={context.today}
        highlightId={highlightId}
        openMenuId={menu ? menu.row.id : null}
        onPrimary={(row, action) => actions.run(row, action)}
        onMenu={openMenu}
        onMenuPress={pressMenu}
      />
    );
  }

  return (
    <Page
      title="Manual deliveries"
      primaryAction={{
        content: "New delivery",
        onAction: () => setPanel({ seed: null }),
        disabled: !context,
      }}
    >
      <div className={toast ? "md-root md-root--toast" : "md-root"}>
        <p className="md-lede">One-off deliveries by your driver or a courier.</p>
        {loadError ? (
          <Banner
            status="critical"
            title="We couldn't load the deliveries"
            action={{ content: "Retry", onAction: () => (context ? load() : loadContext()) }}
          >
            <p>{sentence(loadError)}</p>
          </Banner>
        ) : null}
        {context && range ? (
          <DeliveryFilters
            query={query}
            onQuery={setQuery}
            status={status}
            onStatus={setStatus}
            range={range}
            onRange={setRange}
            visibleCount={visible.length}
            onExport={onExport}
            exporting={exporting}
          />
        ) : null}
        {body}
        {menu ? (
          <RowMenu
            anchorRect={menu.rect}
            items={menuItems(menu.row, role, nowMs)}
            label={`More actions for ${menu.row.customer_name || menu.row.reference}`}
            onSelect={selectMenu}
            onClose={closeMenu}
          />
        ) : null}
        {panel && context ? (
          <NewDeliveryPanel
            shopId={shopId}
            timezone={context.timezone}
            todayIso={context.today}
            seed={panel.seed}
            onClose={() => {
              setPanel(null);
              load(true);
            }}
            onBooked={(result, row) => {
              setPanel(null);
              if (row && row.id) setHighlightId(row.id);
              setToast({
                title: result.title,
                text: result.text,
                action:
                  result.printLabel && row && row.id
                    ? { label: "Print label", run: () => actions.printLabel(row) }
                    : null,
              });
              load(true);
            }}
            onSaved={(kind, rowId) => {
              if (rowId) setHighlightId(rowId);
              setToast(SAVED_TOASTS[kind]);
            }}
          />
        ) : null}
        {confirmElement}
        <Toast toast={toast} besidePanel={!!(panel && context)} onDismiss={() => setToast(null)} />
      </div>
    </Page>
  );
}
